import { useCallback } from 'react';
import { useCampaignEditorContext } from '../state/hooks/useCampaignEditorContext';
import { useParams } from 'react-router';
import useNotifications from 'core/notifications/useNotifications';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { mapStateToCampaign } from '../infrastructure/mapStateToCampaign';
import { useUpdateBulkRetargetingCampaignMutation } from 'sections/bulk-retargeting-campaigns/queries/useUpdateBulkRetargetingCampaign';
import { useCampaignValidator } from './useCampaignValidator';
import { useFeatureFlags } from 'core/app/AppContext';

export const useSaveCampaign = () => {
  const { t } = useTranslation('campaigns');
  const client = useQueryClient();
  const { showNotification } = useNotifications();
  const { siteId, campaignId } = useParams<{ siteId: string; campaignId: string }>();

  const campaignValidation = useCampaignValidator();

  const { state, dispatch } = useCampaignEditorContext();
  const bulkyMutation = useUpdateBulkRetargetingCampaignMutation();
  const { flagEnabled } = useFeatureFlags();
  const isFeatureFlagEnabled = flagEnabled('ld-bulk-date-pickers');

  const saveCampaign = useCallback(async () => {
    try {
      if (!campaignValidation.scheduling.isValid) {
        campaignValidation.scheduling.details.forEach(schedule => {
          schedule.errors.forEach(error => {
            if (error === t('bulkRetargetingCampaigns.editor.review.scheduling.scheduledDateAfterSixMonthsMessage')) {
              showNotification(
                t('bulkRetargetingCampaigns.editor.review.scheduling.scheduledDateAfterSixMonthsMessage'),
                'error',
              );
            }

            if (error === t('bulkRetargetingCampaigns.editor.review.scheduling.scheduledDateBeforeEndDate')) {
              showNotification(
                t('bulkRetargetingCampaigns.editor.review.scheduling.scheduledDateBeforeEndDate'),
                'error',
              );
            }
          });
        });
        return;
      }
      if (!campaignValidation.audience.isValid) {
        let hasShownDateError = false;
        campaignValidation.audience.details.forEach(audience => {
          audience.errors.forEach(error => {
            if (
              error === t('bulkRetargetingCampaigns.editor.review.audience.errors.startDate') ||
              error === t('bulkRetargetingCampaigns.editor.review.audience.errors.endDate')
            ) {
              if (!hasShownDateError) {
                showNotification(t('bulkRetargetingCampaigns.editor.review.audience.errors.datesValidation'), 'error');
                hasShownDateError = true;
              }
            } else {
              // Show other validation errors (including purchase check errors)
              showNotification(error, 'error');
            }
          });
        });
        return;
      }

      await bulkyMutation.mutateAsync({
        siteId,
        campaignId,
        campaign: mapStateToCampaign(siteId, campaignId, state, t, isFeatureFlagEnabled),
      });

      client.invalidateQueries(['bulk-retargeting-campaign', siteId, campaignId]);
      dispatch({ type: 'SET_UNSAVED_CHANGES', payload: false });
      showNotification('Campaign updated', 'normal');
    } catch (error) {
      showNotification(t('bulkRetargetingCampaigns.editor.saveCampaignError'), 'error');
    }
  }, [
    campaignValidation.scheduling.isValid,
    campaignValidation.scheduling.details,
    campaignValidation.audience.isValid,
    campaignValidation.audience.details,
    bulkyMutation,
    siteId,
    campaignId,
    state,
    t,
    isFeatureFlagEnabled,
    client,
    dispatch,
    showNotification,
  ]);

  return {
    saveCampaign,
    ...bulkyMutation,
  };
};
